import Button from '@jetbrains/ring-ui/components/button/button'
import ButtonGroup from '@jetbrains/ring-ui/components/button-group/button-group'
import DropdownMenu from '@jetbrains/ring-ui/components/dropdown-menu/dropdown-menu'
import {skipToken} from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import * as React from 'react'

import {useQueryParam, useSetQueryParams} from '../../../../../hooks/routes'
import {getSingleBuildArg} from '../../../../../rest/builds'
import {getBuildLocator} from '../../../../../rest/locators'
import {restApi} from '../../../../../services/rest'
import type {BuildId} from '../../../../../types'
import {stringifyId, toBuildId} from '../../../../../types'
import {emptyArray} from '../../../../../utils/empty'
import {getPropertyFromList} from '../../../../../utils/getProperty'
import {subscribeOnBuildTypesEvents} from '../../../../../utils/subscriber'
import {BUILD_CHANGED_STATUS} from '../../../../../utils/subscriptionEvents'
import SvgIcon from '../../../../common/SvgIcon/SvgIcon'
import {changePageFullLogBuildId} from '../../../../pages/BuildPage/BuildLogTab/BuildLogTab.actions'

import styles from './BuildLogDependenciesNavigation.css'

import {useAppDispatch} from 'src/hooks/react-redux'

type Props = {
  buildId: BuildId
}

const SELECTED_BUILD_DEPENDENCY_ID_PARAM = 'pageFullLogBuildId'
const VISIBLE_TABS_NUMBER = 6

const BUILD_EVENT_TYPES = [BUILD_CHANGED_STATUS]

export const dependenciesNavigationFields =
  'snapshot-dependencies($locator:(buildType:(project:(virtual:true))),build(id,status,state,buildType(internalId,name),properties(property(name,value),$locator(name:teamcity.build.parallelTests.currentBatch))))'

function BuildLogDependenciesNavigation({buildId}: Props) {
  const dispatch = useAppDispatch()
  const setQueryParams = useSetQueryParams()
  const pageFullLogBuildIdParam = useQueryParam(SELECTED_BUILD_DEPENDENCY_ID_PARAM)
  const selectedDependencyId =
    pageFullLogBuildIdParam != null ? toBuildId(pageFullLogBuildIdParam) : null
  const {dependencies, refetch} = restApi.endpoints.getBuild.useQuery(
    {
      buildLocator: getBuildLocator(buildId),
      fields: dependenciesNavigationFields,
    },
    {
      selectFromResult: ({data}) => ({
        dependencies: data?.['snapshot-dependencies']?.build ?? emptyArray,
      }),
    },
  )

  const batches = React.useMemo(
    () => dependencies.filter(build => build.properties?.property?.length),
    [dependencies],
  )

  React.useEffect(() => {
    if (batches.length > 0) {
      return subscribeOnBuildTypesEvents(
        batches.map(({buildType}) => buildType?.internalId),
        BUILD_EVENT_TYPES,
        refetch,
      )
    }
    return undefined
  }, [batches, refetch])

  const selectedDependencyPresent = batches.some(({id}) => id === selectedDependencyId)
  restApi.endpoints.getBuildNormalized.useQuerySubscription(
    selectedDependencyId != null && selectedDependencyPresent
      ? getSingleBuildArg(getBuildLocator(selectedDependencyId))
      : skipToken,
  )

  React.useEffect(() => {
    if (selectedDependencyId != null) {
      if (selectedDependencyPresent) {
        dispatch(changePageFullLogBuildId(selectedDependencyId))
      }
    } else {
      dispatch(changePageFullLogBuildId(buildId))
    }
  }, [buildId, selectedDependencyPresent, dispatch, selectedDependencyId])

  if (batches.length === 0) {
    return null
  }

  // Move selected dependency to the beginning of the list
  let sortedDependencies = batches
  if (selectedDependencyId != null) {
    const selectedDependencyIndex = batches.findIndex(({id}) => id === selectedDependencyId)
    if (selectedDependencyIndex !== -1 && selectedDependencyIndex > VISIBLE_TABS_NUMBER - 1) {
      const dependenciesCopy = batches.slice()
      const [selectedDependency] = dependenciesCopy.splice(selectedDependencyIndex, 1)
      sortedDependencies = [
        ...dependenciesCopy.slice(0, VISIBLE_TABS_NUMBER - 1),
        selectedDependency,
        ...dependenciesCopy.slice(VISIBLE_TABS_NUMBER - 1),
      ]
    }
  }

  return (
    <div className={styles.container}>
      <ButtonGroup>
        <Button
          active={selectedDependencyId == null || buildId === selectedDependencyId}
          onClick={() => {
            setQueryParams({[SELECTED_BUILD_DEPENDENCY_ID_PARAM]: undefined})
          }}
        >
          {'Current build'}
        </Button>
        {sortedDependencies.slice(0, VISIBLE_TABS_NUMBER).map(({id, status, state, properties}) => {
          const index = getPropertyFromList('teamcity.build.parallelTests.currentBatch', properties)
          return index != null ? (
            <Button
              active={selectedDependencyId != null && id === selectedDependencyId}
              key={id}
              onClick={() => {
                setQueryParams({[SELECTED_BUILD_DEPENDENCY_ID_PARAM]: stringifyId(id)})
              }}
              className={classNames({[styles.failedBuild]: status === 'FAILURE'})}
            >
              {status === 'SUCCESS' && state === 'finished' ? (
                <SvgIcon className={styles.successIcon} icon="ok" />
              ) : status === 'FAILURE' ? (
                <SvgIcon className={styles.failureIcon} icon="exception" />
              ) : null}
              {`Batch ${index}`}
            </Button>
          ) : null
        })}
        {sortedDependencies.length > VISIBLE_TABS_NUMBER ? (
          <DropdownMenu
            data={sortedDependencies
              .slice(VISIBLE_TABS_NUMBER)
              .filter(({buildType}) => buildType?.name != null)
              .map(({id, status, properties}) => {
                const index = getPropertyFromList(
                  'teamcity.build.parallelTests.currentBatch',
                  properties,
                )
                return {
                  className: status === 'FAILURE' ? styles.failedBuild : undefined,
                  label: `Batch ${index}`,
                  onClick: () =>
                    setQueryParams({[SELECTED_BUILD_DEPENDENCY_ID_PARAM]: stringifyId(id)}),
                }
              })}
            anchor={<Button>{'...'}</Button>}
          />
        ) : null}
      </ButtonGroup>
    </div>
  )
}

export default React.memo(BuildLogDependenciesNavigation)
